import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Bell, Plus, RefreshCw, Megaphone, Calendar, Users, X, Search, Trash2, Edit2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Noticeboard({ user, onLogout }) {
  const [notices, setNotices] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'announcement',
    priority: 'normal',
    target_audience: 'all'
  });

  useEffect(() => {
    fetchAllNotices();
    // Auto-refresh every 30 seconds for real-time updates
    const interval = setInterval(fetchAllNotices, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAllNotices = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch from both endpoints
      const [noticesRes, announcementsRes] = await Promise.allSettled([
        axios.get(`${API}/noticeboard/notices`, { headers }),
        axios.get(`${API}/announcements`, { headers })
      ]);

      const noticesList = noticesRes.status === 'fulfilled' ? (noticesRes.value.data.notices || []) : [];
      const announcementsList = announcementsRes.status === 'fulfilled' ? (announcementsRes.value.data || []) : [];

      setNotices(noticesList);
      setAnnouncements(announcementsList);
    } catch (error) {
      console.error('Error fetching notices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      // Try to post to both noticeboard and announcements for broader reach
      await Promise.allSettled([
        axios.post(`${API}/noticeboard/notices`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.post(`${API}/announcements`, {
          title: formData.title,
          content: formData.content,
          priority: formData.priority.charAt(0).toUpperCase() + formData.priority.slice(1),
          target_audience: ['All'],
          is_active: true
        }, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      alert('✅ Notice posted successfully! All employees will be notified.');
      setShowForm(false);
      resetForm();
      fetchAllNotices();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, isAnnouncement = false) => {
    if (!window.confirm('Are you sure you want to delete this notice?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const endpoint = isAnnouncement ? `${API}/announcements/${id}` : `${API}/noticeboard/notices/${id}`;
      
      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Notice deleted successfully!');
      fetchAllNotices();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      category: 'announcement',
      priority: 'normal',
      target_audience: 'all'
    });
  };

  const getPriorityColor = (priority) => {
    const p = priority?.toLowerCase();
    switch (p) {
      case 'urgent': return 'bg-red-100 text-red-700 border-l-red-500';
      case 'high': return 'bg-orange-100 text-orange-700 border-l-orange-500';
      case 'normal': return 'bg-blue-100 text-blue-700 border-l-blue-500';
      case 'low': return 'bg-gray-100 text-gray-700 border-l-gray-400';
      default: return 'bg-blue-100 text-blue-700 border-l-blue-500';
    }
  };

  const getPriorityBadgeColor = (priority) => {
    const p = priority?.toLowerCase();
    switch (p) {
      case 'urgent': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'normal': return 'bg-blue-500 text-white';
      case 'low': return 'bg-gray-400 text-white';
      default: return 'bg-blue-500 text-white';
    }
  };

  // Combine and deduplicate notices
  const allNotices = [
    ...notices.map(n => ({ ...n, source: 'noticeboard' })),
    ...announcements.map(a => ({ ...a, source: 'announcement', category: 'announcement' }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Filter notices
  const filteredNotices = allNotices.filter(notice => {
    const matchesSearch = notice.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          notice.content?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = filterPriority === 'all' || notice.priority?.toLowerCase() === filterPriority;
    return matchesSearch && matchesPriority;
  });

  const canPost = user?.role?.toLowerCase() === 'admin' || 
                  user?.role?.toLowerCase() === 'hr' || 
                  user?.role?.toLowerCase() === 'hr manager';

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>📢 Noticeboard</h1>
            <p className="text-gray-600 mt-2">Company announcements and updates • Auto-refreshes every 30 seconds</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchAllNotices}
              disabled={loading}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {canPost && (
              <button
                onClick={() => setShowForm(true)}
                className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
                style={{backgroundColor: '#2b3380'}}
              >
                <Plus className="w-5 h-5" />
                Post Notice
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full" style={{backgroundColor: '#e8eaf6'}}>
              <Megaphone className="w-6 h-6" style={{color: '#2b3380'}} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{allNotices.length}</p>
              <p className="text-sm text-gray-500">Total Notices</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-red-100">
              <Bell className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">
                {allNotices.filter(n => n.priority?.toLowerCase() === 'urgent' || n.priority?.toLowerCase() === 'high').length}
              </p>
              <p className="text-sm text-gray-500">High Priority</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-green-100">
              <Calendar className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">
                {allNotices.filter(n => {
                  const created = new Date(n.created_at);
                  const today = new Date();
                  return created.toDateString() === today.toDateString();
                }).length}
              </p>
              <p className="text-sm text-gray-500">Today</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-blue-100">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">All</p>
              <p className="text-sm text-gray-500">Target Audience</p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search notices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Priorities</option>
              <option value="urgent">🔴 Urgent</option>
              <option value="high">🟠 High</option>
              <option value="normal">🔵 Normal</option>
              <option value="low">⚪ Low</option>
            </select>
          </div>
        </div>

        {/* Notices List */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            <p className="mt-3 text-gray-500">Loading notices...</p>
          </div>
        ) : filteredNotices.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Bell className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-xl font-medium text-gray-500 mb-2">No notices available</p>
            <p className="text-gray-400">
              {searchTerm || filterPriority !== 'all' 
                ? 'Try different search terms or filters' 
                : canPost ? 'Click "Post Notice" to create one' : 'Check back later for updates'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredNotices.map((notice) => (
              <div 
                key={notice.id} 
                className={`bg-white rounded-lg shadow-md p-6 border-l-4 hover:shadow-lg transition-shadow ${getPriorityColor(notice.priority)}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold" style={{color: '#2b3380'}}>{notice.title}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityBadgeColor(notice.priority)}`}>
                        {notice.priority?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  {canPost && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDelete(notice.id, notice.source === 'announcement')}
                        className="p-2 hover:bg-red-50 rounded text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                
                <p className="text-gray-700 mb-4 whitespace-pre-wrap leading-relaxed">{notice.content}</p>
                
                <div className="flex flex-wrap justify-between items-center text-sm text-gray-500 pt-3 border-t">
                  <div className="flex items-center gap-4">
                    <span className="px-2 py-1 bg-gray-100 rounded text-xs">{notice.category || 'Announcement'}</span>
                    {notice.target_audience && (
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {Array.isArray(notice.target_audience) ? notice.target_audience.join(', ') : notice.target_audience}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date(notice.created_at).toLocaleString('en-IN', {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Post Notice Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>📢 Post New Notice</h2>
                  <button onClick={() => { setShowForm(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Title *</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Holiday Announcement"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Content *</label>
                      <textarea
                        value={formData.content}
                        onChange={(e) => setFormData({...formData, content: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        rows="5"
                        placeholder="Write your announcement here..."
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Category</label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({...formData, category: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="announcement">📢 Announcement</option>
                          <option value="hr_update">👥 HR Update</option>
                          <option value="policy">📋 Policy</option>
                          <option value="event">🎉 Event</option>
                          <option value="urgent">🚨 Urgent</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Priority</label>
                        <select
                          value={formData.priority}
                          onChange={(e) => setFormData({...formData, priority: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="low">⚪ Low</option>
                          <option value="normal">🔵 Normal</option>
                          <option value="high">🟠 High</option>
                          <option value="urgent">🔴 Urgent</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Target Audience</label>
                      <select
                        value={formData.target_audience}
                        onChange={(e) => setFormData({...formData, target_audience: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="all">👥 All Employees</option>
                        <option value="department">🏢 Specific Department</option>
                      </select>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-700">
                        <strong>Note:</strong> This notice will be visible to all employees immediately and they will receive email notifications.
                      </p>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="px-6 py-3 text-white rounded-lg font-medium flex items-center gap-2"
                        style={{backgroundColor: submitting ? '#9e9e9e' : '#2b3380'}}
                      >
                        <Megaphone className="w-5 h-5" />
                        {submitting ? 'Posting...' : 'Post Notice'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowForm(false); resetForm(); }}
                        className="px-6 py-3 border rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Noticeboard;
